import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { AIInsightBanner } from "../components/AIInsightBanner";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { fmtINR, fmtNum } from "../lib/format";
import { Building2, MapPin, X } from "lucide-react";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";

export default function ProjectIntelligence() {
  const { asOf, projectsQS } = usePeriod();
  const [projects, setProjects] = useState([]);
  const [exec, setExec] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/projects/all").then((r) => setProjects(r.data.projects));
    if (asOf) api.get(`/kpi/executive?as_of=${asOf}&period=YTD${projectsQS}`).then((r) => setExec(r.data));
  }, [asOf, projectsQS]);

  const psales = exec?.project_sales || [];
  const pcoll = exec?.project_collection || [];
  const merged = projects.map((p) => {
    const s = psales.find((x) => x.project_name === p.project_name);
    const c = pcoll.find((x) => x.project_name === p.project_name);
    return { ...p, sales: s, collection: c?.collection || 0 };
  });

  const projectDetail = selected ? (
    exec?.monthly_trend?.map((m) => ({ ...m })) || []
  ) : [];

  return (
    <>
      <Header title="Project Intelligence" subtitle="Portfolio Health Snapshot" />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <AIInsightBanner context={{ fy_label: exec?.fy_label, kpis: exec?.kpis, top_projects: psales.slice(0, 5) }} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {merged.map((p) => (
            <button
              key={p.project_name}
              onClick={() => setSelected(p)}
              className="bg-white border border-slate-200 rounded-md p-4 text-left hover:border-slate-400 transition-colors group"
              data-testid={`project-card-${p.project_name.replace(/\s+/g, "-")}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate group-hover:text-vexen-sidebar">{p.project_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-1 flex items-center gap-1">
                    <MapPin size={10} /> {p.city}
                  </div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono uppercase ${
                  p.status === "Active" ? "bg-emerald-50 text-emerald-700" :
                  p.status === "Pre-launch" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"
                }`}>{p.status}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Net Sales</div>
                  <div className="font-mono font-semibold">{fmtINR(p.sales?.net || 0, { short: true })}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Units</div>
                  <div className="font-mono font-semibold">{fmtNum(p.sales?.count || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Collection</div>
                  <div className="font-mono font-semibold">{fmtINR(p.collection || 0, { short: true })}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Type</div>
                  <div className="text-[11px] truncate">{p.project_type}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {selected && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="project-detail-modal"
          >
            <div
              className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">
                    Project Detail · {selected.city}
                  </div>
                  <div className="text-lg font-bold font-display">{selected.project_name}</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-900" data-testid="close-project-detail">
                  <X size={18} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Net Sales</div>
                    <div className="text-xl font-mono font-bold mt-1">{fmtINR(selected.sales?.net || 0, { short: true })}</div>
                  </div>
                  <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Collection</div>
                    <div className="text-xl font-mono font-bold mt-1">{fmtINR(selected.collection || 0, { short: true })}</div>
                  </div>
                  <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Units Sold</div>
                    <div className="text-xl font-mono font-bold mt-1">{fmtNum(selected.sales?.count || 0)}</div>
                  </div>
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Monthly Trend</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={projectDetail}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
                    <Bar dataKey="net_sales" fill="#0F1D32" name="Net Sales" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
